import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import { failureError, notice } from '@/components/pbo/notice'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { ShipperOptionSource, buildShipperNameOptions } from '@/components/system/shipperSelectFilter'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { canSelectShipper, defaultShipper, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import { getShipperListV2 } from '@/services/shipper/apis'
import { ShipperItem } from '@/services/shipper/types'
import { toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import { useRowMediaFiles } from '@/services/commonFile/useRowMediaFiles'
import { cancelInbound } from '@/services/inbound/inboundInquiry/apis'
import { useLesInboundCancelGate } from '@/services/inbound/lesOrigin/useLesInboundCancelGate'
import {
  RETURN_REPORT_ITEM_EXCEL_URL,
  RETURN_REPORT_SUMMARY_EXCEL_URL,
  resendReturnInboundPlan,
  restoreReturnInbound,
  saveReturnReport,
} from '@/services/return/returnInquiry/apis'
import { returnInquiryQueryKeys, useGetReturnReportItems, useGetReturnReportSummaries } from '@/services/return/returnInquiry/useReturnInquiryQueries'
import { ExcelDownloadReason, GetReturnReportSummariesReq, ReturnReportSummary } from '@/services/return/returnInquiry/types'
import {
  CANCELED_STATUS,
  DEFAULT_SERVICE_TYPE,
  HeadRow,
  ItemRow,
  MESSAGES,
  Rgr02FilterValues,
  actionFailureMessage,
  fromSearchQuery,
  hasShipperScope,
  keepOwnShipperRows,
  pickList,
  summaryActionBlockReason,
  toHeadRow,
  toItemReq,
  toItemRow,
  toSearchQuery,
  toSummariesReq,
  withDefaultDateRange,
} from '../rules'

/** 생성본 컨펌 모달 — 문구는 index.tsx(생성본)가, 실행(onOk)은 이 훅이 넘긴다. */
export type ConfirmFn = (onOk: () => Promise<void>, count?: number) => unknown

type MediaKind = 'video' | 'attach'
type DownloadTarget = 'head' | 'item'

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message || fallback

/**
 * RGR02 반품조회 화면 훅 — 조회영역 값·HEAD/ITEM 조회·입고취소·원복·재전송·저장·엑셀·첨부·조회조건 세션 유지를 모은다.
 * 화면 구조(생성본)는 index.tsx 가 그대로 들고, 이 훅은 그 TODO(dev) 자리에 꽂을 값과 동작만 준다.
 */
export const useRgr02Screen = (coMingleEnabled = false) => {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()

  // 재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고르고 기본값은 MUSINSA 다. 고른 화주 그대로 조회한다(LOGISPRD-15725, `withCoMingle`)
  const { isFreeShipper, ownShipperName, ownShipperCode, scope, selectable, defaultShipperName } = useMemo(() => {
    const user = AuthStorage.user.get()
    const scope = withCoMingle(shipperScopeOf(user), coMingleEnabled) // 잠금 판정은 2.0 공용 규칙 — FREE_SHIPPERS 4종 외는 자기 화주로 고정(LOGISPRD-14652)
    const ownName = user.shipperName ? String(user.shipperName) : ''
    // 화주 목록 요청·화주명 값은 화주명이다 — 자기 화주는 세션 화주명(shipperName)이다(gr02V2 준용)
    const selectable = selectableShippers(scope).map((s) => ({ shipperCode: s.shipperCode, shipperName: s.shipperCode === scope.shipperCode ? ownName : s.shipperName }))
    return {
      isFreeShipper: !scope.locked,
      ownShipperName: ownName,
      ownShipperCode: scope.shipperCode,
      scope,
      selectable,
      defaultShipperName: selectable.find((s) => s.shipperCode === defaultShipper(scope).shipperCode)?.shipperName ?? ownName,
    }
  }, [coMingleEnabled])
  const selectableNames = useMemo(() => selectable.map((s) => s.shipperName).filter(Boolean), [selectable])

  // ── 화주 옵션 — 화주 고정 계정은 목록 요청·옵션을 고를 수 있는 화주로 좁힌다(gr02 준용) ──────────────
  const { data: shippers } = useQuery<ShipperItem[]>({
    queryKey: returnInquiryQueryKeys.shippers(isFreeShipper ? 'ALL' : selectableNames.join(',')),
    queryFn: async () => (await getShipperListV2({ shipperNames: isFreeShipper ? [] : selectableNames, logisticsServiceTypes: [], page: 0, size: 10000 }))?.data?.items ?? [],
    enabled: isFreeShipper || selectableNames.length > 0,
    staleTime: API_COMMON_CACHE_TIME,
  })
  const shipperSources = useMemo<ShipperOptionSource[]>(
    () => (isFreeShipper ? shippers ?? [] : (shippers ?? []).filter((s) => selectable.some((c) => c.shipperCode === s.shipperCode))),
    [shippers, isFreeShipper, selectable],
  )
  /**
   * 화주 고정 계정이 고른 화주의 서비스유형 — 유형 셀렉트를 이 값 하나로 좁히고 기본값으로 쓴다.
   * BE `shipper.shipper_code` 는 유일 키(uk_shipper_code)이고 service_type 은 단일 컬럼이라 코드로 찾으면 한 건이다.
   */
  const serviceTypeOf = useCallback(
    (name: string | undefined): string | undefined => {
      if (isFreeShipper) return undefined
      const code = selectable.find((s) => s.shipperName === name)?.shipperCode
      return shipperSources.find((s) => s.shipperCode === code)?.logisticsServiceType || undefined
    },
    [isFreeShipper, selectable, shipperSources],
  )
  /** 화주 고정 계정의 화주명 값 — 고를 수 있는 화주 하나만 남기고, 없으면 기본 화주다 */
  const lockedShipperName = useCallback(
    (names: string[] | undefined): string => (names ?? []).find((n) => selectableNames.includes(n)) ?? defaultShipperName,
    [selectableNames, defaultShipperName],
  )
  const ownServiceType = serviceTypeOf(ownShipperName)

  /**
   * 진입·초기화 기본값 — 계정 기준(MUSINSA = M1P/MUSINSA, 티켓 디폴트 조회조건) + 입고예정일자 오늘.
   * 입고예정일자를 여기서 채운다 — 날짜 칸(DateRangeField)의 기본값 effect 는 필터를 접으면 그려지지 않아 돌지 않는다.
   */
  const buildDefaultFilter = useCallback(
    (): Rgr02FilterValues => ({
      service_type: isFreeShipper ? [DEFAULT_SERVICE_TYPE] : [serviceTypeOf(defaultShipperName) ?? ''],
      shipper_name: [defaultShipperName],
      PLN_DATE: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
    }),
    [isFreeShipper, serviceTypeOf, defaultShipperName],
  )

  // ── 조회조건 세션 유지(menuOnSession) — 마운트 때 URL 에서 한 번만 읽는다(gr02 D16 방식) ──────
  const [restored] = useState<Rgr02FilterValues | undefined>(() => {
    const r = fromSearchQuery(parsedQs)
    if (!r) return r
    // 날짜가 빠진 조건(형식이 어긋나면 fromSearchQuery 가 버린다)으로 들어오면 진입 즉시 전건 조회가 나간다 — 기본 기간으로 채운다.
    const dated = withDefaultDateRange(r, dayjs().format('YYYY-MM-DD'))
    if (isFreeShipper) return dated
    // URL 을 손대 남의 화주 조건으로 들어오는 것을 막는다 — 고를 수 있는 화주가 아니면 기본 화주로 덮고, 유형은 목록 응답 뒤 아래 effect 가 채운다.
    return { ...dated, shipper_name: [lockedShipperName(dated.shipper_name)], service_type: [''] }
  })
  const [filter, setFilter] = useState<Rgr02FilterValues>(() => restored ?? buildDefaultFilter())
  const [filterResetToken, setFilterResetToken] = useState(0)

  // 화주 고정 계정의 유형은 화주 목록 응답 뒤에야 안다 — 유형이 비어 있으면 고른 화주의 유형으로 채운다.
  useEffect(() => {
    setFilter((f) => {
      const type = serviceTypeOf(lockedShipperName(f.shipper_name))
      return !type || pickList(f.service_type) ? f : { ...f, service_type: [type] }
    })
  }, [serviceTypeOf, lockedShipperName])

  const typesKey = (pickList(filter.service_type) ?? []).join('\n')
  const shipperNameOptions = useMemo(
    () => buildShipperNameOptions(shipperSources, { valueKey: 'shipperName', serviceTypes: typesKey ? typesKey.split('\n') : [] }),
    [shipperSources, typesKey],
  )

  /** 화주유형을 바꾸면 그 유형에 없는 화주명 선택을 걷어낸다(LOGISPRD-12609 공통 규칙). 목록 응답 전에는 좁힐 근거가 없어 두고 본다. */
  const changeServiceTypes = useCallback(
    (types: string[]) => {
      setFilter((f) => {
        if (!shipperSources.length) return { ...f, service_type: types }
        const allowed = new Set(buildShipperNameOptions(shipperSources, { valueKey: 'shipperName', serviceTypes: types.filter(Boolean) }).map((o) => o.value))
        const names = (f.shipper_name ?? []).filter((n) => n === '' || allowed.has(n))
        return { ...f, service_type: types, shipper_name: names.length ? names : [''] }
      })
    },
    [shipperSources],
  )

  // ── HEAD 조회 ────────────────────────────────────────────────────
  const [condition, setCondition] = useState<GetReturnReportSummariesReq | null>(null)
  /** 화주 고정 계정이 마지막으로 조회한 화주코드 — 응답 행을 이 화주로 거른다 */
  const [searchedShipperCode, setSearchedShipperCode] = useState(() => defaultShipper(scope).shipperCode)
  const [searchSeq, setSearchSeq] = useState(0)
  const [selectedHead, setSelectedHead] = useState<ReturnReportSummary | null>(null)
  const [selectionResetToken, setSelectionResetToken] = useState(0)
  const [itemSelectionResetToken, setItemSelectionResetToken] = useState(0)

  const { data: summaries, isFetching: isHeadFetching, error: headError } = useGetReturnReportSummaries(condition, searchSeq)
  // 조회 실패를 '데이터 없음'으로 두지 않고 알린다(LOGISPRD-14909) — 실패마다 error 객체가 새로 와서 조회 한 번에 한 번 뜬다.
  useEffect(() => {
    if (headError) notice.failure(apiErrorText(headError, MESSAGES.searchFailed))
  }, [headError])
  // 화주 고정 계정은 응답도 조회한 화주코드로 거른다 — 조회 조건의 화주명이 같아도 다른 화주코드 행이 섞여 오고, 그 행이 입고취소·원복·재전송 대상이 된다.
  const headRows = useMemo(() => {
    const rows = summaries ?? []
    return (isFreeShipper ? rows : keepOwnShipperRows(rows, searchedShipperCode)).map(toHeadRow)
  }, [summaries, isFreeShipper, searchedShipperCode])

  /** 목록을 다시 받는다 — HEAD 선택·ITEM·ITEM 선택도 비운다(처리 뒤 이전 행이 대상으로 남지 않게). */
  const refreshHead = useCallback(() => {
    setSelectedHead(null)
    setSelectionResetToken((t) => t + 1)
    setItemSelectionResetToken((t) => t + 1)
    setSearchSeq((s) => s + 1)
  }, [])

  /** 조회 — 조건을 메뉴 세션·URL 에 남기고(reload 로 탭바 갱신) 목록을 받는다. */
  const runSearch = useCallback(
    (input: Rgr02FilterValues) => {
      // 화주 고정 계정은 화주명을 고를 수 있는 화주 하나로 고정한다 — 화주명 '전체'로 두면 유형만 남아 같은 유형의 다른 화주까지 조회된다(BE 는 계정으로 거르지 않는다).
      //  고를 화주가 둘 이상이면 유형도 고른 화주의 것으로 보낸다 — 화주를 바꾼 직후 칸에 남은 이전 화주 유형을 싣지 않는다(gr02V2 준용).
      const name = lockedShipperName(input.shipper_name)
      const lockedType = canSelectShipper(scope) ? serviceTypeOf(name) : undefined
      const values = isFreeShipper ? input : { ...input, shipper_name: [name], ...(lockedType ? { service_type: [lockedType] } : {}) }
      const req = toSummariesReq(values)
      // 검증 실패 = false — 컴포넌트 모드 뷰는 false 일 때만 페이저·선택·마스터 pick 리셋을 건너뛴다(가이드 52 규칙 10).
      if (!hasShipperScope(req)) {
        notice.invalid(MESSAGES.requiredShipper)
        return false
      }
      const query = toSearchQuery(values)
      MenuStorage.menuOnSession.setSearch(query)
      reload()
      replaceQuery(query)
      setCondition(req)
      if (!isFreeShipper) setSearchedShipperCode(selectable.find((s) => s.shipperName === name)?.shipperCode ?? ownShipperCode)
      refreshHead()
      return true
    },
    [isFreeShipper, lockedShipperName, scope, serviceTypeOf, selectable, ownShipperCode, reload, replaceQuery, refreshHead],
  )
  /** 조회 — 컴포넌트 모드 뷰는 조회영역 값을 인자로 준다(뷰가 값을 소유). 인자가 없으면 훅이 든 filter 로(옛 entry 경로). */
  const search = useCallback((values?: Rgr02FilterValues) => runSearch(values ?? filter), [runSearch, filter])

  // 복원된 조건이 있으면 진입 즉시 한 번 조회한다 — 목록까지 되살아나야 "보던 화면으로 돌아온다".
  //  화주를 고르는 잠금 계정(재고통합)은 화주 목록이 온 뒤 조회한다 — 고른 화주의 유형을 목록에서 알아야 조회 조건·URL 에 싣는다(LOGISPRD-15725).
  const waitsShippers = !isFreeShipper && canSelectShipper(scope)
  const isRestoreSearchedRef = useRef(false)
  useEffect(() => {
    if (!restored || isRestoreSearchedRef.current || (waitsShippers && !shippers)) return
    isRestoreSearchedRef.current = true
    runSearch(restored)
  }, [restored, runSearch, waitsShippers, shippers])

  /** 초기화 — 필터를 계정 기본값으로 되돌리고 남긴 조건·두 표·선택을 모두 비운다. */
  const reset = useCallback(() => {
    MenuStorage.menuOnSession.setSearch({})
    reload()
    replaceQuery({})
    setFilter(buildDefaultFilter())
    setFilterResetToken((t) => t + 1)
    setCondition(null)
    setSelectedHead(null)
    setSelectionResetToken((t) => t + 1)
    setItemSelectionResetToken((t) => t + 1)
  }, [buildDefaultFilter, reload, replaceQuery])

  // ── ITEM — HEAD 행을 한 번 클릭하면 그 행 키로 조회 ─────────────────────
  const itemReq = useMemo(() => (selectedHead ? toItemReq(selectedHead) : null), [selectedHead])
  const { data: itemsResponse, isFetching: isItemFetching, refetch: refetchItems } = useGetReturnReportItems(itemReq)
  // ITEM 조회도 화주명으로 나가므로(toItemReq) HEAD 와 같은 이유로 응답을 조회한 화주코드로 거른다.
  const items = useMemo(
    () => (isFreeShipper || !itemsResponse ? itemsResponse : keepOwnShipperRows(itemsResponse, searchedShipperCode)),
    [itemsResponse, isFreeShipper, searchedShipperCode],
  )

  /** 마스터 pick 해제(뷰의 [조회]·[초기화]·확인창 뒷정리가 onPick([]) 으로 알린다) — 상세를 비운다. */
  const clearHeadPick = useCallback(() => {
    setItemSelectionResetToken((t) => t + 1)
    setSelectedHead(null)
  }, [])

  const selectHeadRow = useCallback((row: HeadRow) => {
    setItemSelectionResetToken((t) => t + 1)
    if (!toItemReq(row.raw)) {
      // 이전 행의 상세를 비운다 — 남겨 두면 방금 누른 행의 상세로 오인한다.
      setSelectedHead(null)
      return notice.invalid(MESSAGES.itemKeyMissing)
    }
    return setSelectedHead(row.raw)
  }, [])

  // 첨부·동영상 개수 — ITEM 의 fileId 를 모아 한 번에 받는다(LOGISPRD-12443 공용). 파일 API 의 shipper 는 화주코드다.
  const itemShipperCode = selectedHead?.sprNm ?? ''
  const fileIds = useMemo(() => (items ?? []).map((i) => i.fileId).filter((v): v is string => Boolean(v)), [items])
  const { filesByFileId, counts: mediaCounts } = useRowMediaFiles('RGR20', itemShipperCode, fileIds)

  // 특이사항 편집값 — 행 키별로 따로 든다. 첨부 개수가 늦게 와 행을 다시 만들어도 편집이 지워지지 않고, 새로 조회되면 버린다.
  const [procEdits, setProcEdits] = useState<Record<string, string>>({})
  useEffect(() => {
    setProcEdits({})
  }, [items])
  const itemRows = useMemo(
    () =>
      (items ?? []).map((i, idx) => {
        const row = toItemRow(i, idx, i.fileId ? mediaCounts.get(i.fileId) : undefined)
        return row.key in procEdits ? { ...row, PROC_NM: procEdits[row.key] } : row
      }),
    [items, mediaCounts, procEdits],
  )
  /** core.Table onRowsChange — 지금 그려진 행(페이지·검색으로 걸러진 부분)만 오므로 키별로 합친다. */
  const onItemRowsChange = useCallback((rows: ItemRow[]) => {
    setProcEdits((prev) => ({ ...prev, ...Object.fromEntries(rows.map((r) => [r.key, ((r.PROC_NM as string | null | undefined) ?? '')])) }))
  }, [])

  const [mediaTarget, setMediaTarget] = useState<{ kind: MediaKind; row: { id: number; fileId: string | null } } | null>(null)
  const openMedia = useCallback((kind: MediaKind, row: ItemRow) => {
    if (row.raw.id == null) return
    setMediaTarget({ kind, row: { id: row.raw.id, fileId: row.raw.fileId } })
  }, [])
  const closeMedia = useCallback(() => setMediaTarget(null), [])

  // ── 입고취소 — 선택 행마다 순서대로 POST /api/v1/cancel-inbound(GR02 공유) ────────────────
  const lesGate = useLesInboundCancelGate()
  /** 입고취소를 막는 이유 — 키 누락·이미 취소·LES 원천. 없으면 null. 확인창을 열기 전(뷰 opens 가드)과 실행 직전 둘 다 같은 판정을 쓴다. */
  const cancelBlockReason = useCallback(
    (rows: HeadRow[]): string | null => {
      const targets = rows.map((r) => r.raw)
      if (!targets.length) return MESSAGES.cancelKeyMissing
      if (!targets.every((s) => s.sprNm && s.clsDate && s.cutSeq != null && s.ordOptNo)) return MESSAGES.cancelKeyMissing
      // BE CancelInboundValidation 미러 — 취소된 행은 400 으로 거절되므로 요청 전에 막는다(LOGISPRD-14915).
      if (targets.some((s) => s.ordStatus === CANCELED_STATUS)) return MESSAGES.cancelAlreadyCanceled
      return lesGate.blockedReason(targets)
    },
    [lesGate],
  )
  const cancel = useCallback(
    (rows: HeadRow[], confirm: ConfirmFn) => {
      const blocked = cancelBlockReason(rows)
      if (blocked) return notice.invalid(blocked)
      // 키는 cancelBlockReason 이 전건 확인했다. cutDate 는 HEAD clsDate(yyyyMMdd) 그대로다.
      const bodies = rows.flatMap(({ raw: s }) =>
        s.sprNm && s.clsDate && s.cutSeq != null && s.ordOptNo
          ? [{ shipperName: s.sprNm, cutDate: s.clsDate, cutSequence: s.cutSeq, orderOptionNo: s.ordOptNo }]
          : [],
      )
      return confirm(async () => {
        const fails: string[] = []
        // 전건을 끝까지 시도한다 — 중간 실패로 멈추지 않는다(gr02 runCancel 준용).
        for (const body of bodies) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await cancelInbound(body, 'RGR20')
          } catch (error) {
            fails.push(`${body.orderOptionNo}: ${errorMessage(error, MESSAGES.cancelFailed)}`)
          }
        }
        if (fails.length) notice.failure([MESSAGES.cancelResult(bodies.length - fails.length, fails.length), ...fails].join('\n'))
        else notice.success(MESSAGES.cancelResult(bodies.length, 0))
        refreshHead()
      })
    },
    [cancelBlockReason, refreshHead],
  )

  // ── 원복·재전송 — 1행 선택에서만(버튼이 막는다). 요청은 {shipperCode, summaryIds:[1개]} ──────────
  const runSummaryAction = useCallback(
    (action: 'restore' | 'resend', rows: HeadRow[], confirm: ConfirmFn) => {
      if (rows.length !== 1) return undefined
      const s = rows[0].raw
      const blocked = summaryActionBlockReason(s, action)
      if (blocked) return notice.invalid(blocked)
      // summaryActionBlockReason 이 둘 다 있음을 이미 확인했다 — 여기서는 타입만 좁힌다.
      const { sprNm, summaryId } = s
      if (!sprNm || summaryId == null) return undefined
      return confirm(async () => {
        const body = { shipperCode: sprNm, summaryIds: [summaryId] }
        try {
          await (action === 'restore' ? restoreReturnInbound(body) : resendReturnInboundPlan(body))
          notice.success(action === 'restore' ? '원복 되었습니다.' : '1건 재전송 되었습니다.')
        } catch (error) {
          notice.failure(actionFailureMessage(error))
        }
        refreshHead()
      }, 1)
    },
    [refreshHead],
  )
  const restore = useCallback((rows: HeadRow[], confirm: ConfirmFn) => runSummaryAction('restore', rows, confirm), [runSummaryAction])
  const resend = useCallback((rows: HeadRow[], confirm: ConfirmFn) => runSummaryAction('resend', rows, confirm), [runSummaryAction])

  // ── 저장(ITEM) — 선택 행의 특이사항(procNm)만 PATCH ────────────────────────────────
  /** 선택 행 중 저장 본문으로 갈 수 있는 것(id·화주코드 있음) — 편집값은 행 상태의 PROC_NM. 뷰의 [저장] 가드도 이 수로 판정한다. */
  const saveTargets = useCallback(
    (rows: ItemRow[]) => {
      const keys = new Set(rows.map((r) => r.key))
      return itemRows.flatMap((r) =>
        keys.has(r.key) && r.raw.id != null && r.raw.sprNm
          ? [{ id: r.raw.id, shipperCode: r.raw.sprNm, procNm: (r.PROC_NM as string | null | undefined) ?? '' }]
          : [],
      )
    },
    [itemRows],
  )
  const save = useCallback(
    (rows: ItemRow[], confirm: ConfirmFn) => {
      const patches = saveTargets(rows)
      if (!patches.length) return undefined
      return confirm(async () => {
        try {
          await saveReturnReport({ items: patches })
          notice.success(`${patches.length}개 저장 완료`)
          setItemSelectionResetToken((t) => t + 1)
          await refetchItems()
        } catch (error) {
          // 알럿 뒤 던진다 — 확인창 슬롯이 거부되면 생성본이 확인창·하단 체크 선택을 남겨 다시 시도할 수 있다(LOGISPRD-14915).
          throw failureError(errorMessage(error, MESSAGES.saveFailed))
        }
      }, patches.length)
    },
    [saveTargets, refetchItems],
  )

  // ── 엑셀 다운로드 — 사유코드는 2.0 콤보(DOWNLOAD_CD), 응답은 암호 zip ──────────────────────
  const { data: combos } = useGetCombos(['DOWNLOAD_CD'], { errorToast: true })
  const reasonOptions = useMemo(() => toSelectOptions(combos?.DOWNLOAD_CD), [combos])
  const [downloadTarget, setDownloadTarget] = useState<DownloadTarget | null>(null)
  const { onDownloadExcel } = useExcelDownload<Record<string, unknown>>({ fileNamePrefix: '반품조회', config: { method: 'post' } })

  // 빈건수는 화면에 떠 있는 행 수로 본다 — 목록 검색으로 0건이 된 표에서 [엑셀 다운로드] 가 열리면 안 된다.
  const openDownload = useCallback((target: DownloadTarget, visibleRowCount: number) => {
    if (visibleRowCount === 0) return notice.invalid(MESSAGES.excelNoRows)
    return setDownloadTarget(target)
  }, [])
  const closeDownload = useCallback(() => setDownloadTarget(null), [])

  /**
   * HEAD 는 마지막 조회 조건, ITEM 은 고른 HEAD 행의 키로 서버에서 다시 뽑는다.
   * 표 위 목록 검색어는 받아 둔 행에만 거는 화면 기능이라 엑셀에는 반영되지 않는다 — 검색으로 좁힌 상태에서도 조회 조건 전건이 내려온다.
   */
  const confirmDownload = useCallback(
    (reason: ExcelDownloadReason) => {
      const target = downloadTarget
      setDownloadTarget(null)
      if (target === 'head' && condition) return onDownloadExcel(RETURN_REPORT_SUMMARY_EXCEL_URL, { ...condition, ...reason })
      if (target === 'item' && itemReq) return onDownloadExcel(RETURN_REPORT_ITEM_EXCEL_URL, { ...itemReq, ...reason })
      return undefined
    },
    [downloadTarget, condition, itemReq, onDownloadExcel],
  )

  const defaultFilter = useMemo(() => buildDefaultFilter(), [buildDefaultFilter])

  return {
    filter,
    setFilter,
    filterResetToken,
    /** 계정 기준 기본 조회 조건(진입·[초기화]) — 컴포넌트 모드 뷰의 filterDefaults 가 쓴다. */
    defaultFilter,
    /** URL 에서 복원한 조회 조건(없으면 undefined) — 뷰의 filterInitial 이 기본값 위에 얹는다. */
    restoredFilter: restored,
    isFreeShipper,
    ownShipperName,
    ownServiceType,
    /** 화주명을 고를 수 있는가 — 재고통합 ON 의 잠긴 29CM·INOOINT(LOGISPRD-15725) */
    canSelectShipper: canSelectShipper(scope),
    /** 화주 고정 계정이 고른 화주명(고를 수 없는 값이면 기본 화주)의 유형 — 화주 목록이 오기 전엔 undefined */
    lockedServiceTypeOf: (names: string[] | undefined) => serviceTypeOf(lockedShipperName(names)),
    /** 화주 옵션 원본(계정 스코프 적용) — 뷰의 화주명 옵션 슬롯이 현재 화주유형으로 좁혀 쓴다. */
    shipperSources,
    shipperNameOptions,
    changeServiceTypes,
    search,
    reset,

    headRows,
    isHeadFetching,
    selectionResetToken,
    // 목록 검색으로 행을 숨길 때 부른다. pbo-core 0.8.1 Table 은 선택 키를 표 안에 들고 dataSource 가 바뀌어도 풀지 않고,
    // 선택 행 목록은 선택이 바뀔 때만 다시 모은다 — 체크한 뒤 숨긴 행이 개수와 입고취소 대상에 그대로 남는다.
    clearHeadSelection: () => setSelectionResetToken((t) => t + 1),
    selectHeadRow,
    clearHeadPick,
    selectedHeadKey: selectedHead ? toHeadRow(selectedHead).key : null,
    cancelBlockReason,
    cancel,
    restore,
    resend,

    itemRows,
    isItemFetching,
    itemSelectionResetToken,
    clearItemSelection: () => setItemSelectionResetToken((t) => t + 1),
    onItemRowsChange,
    saveTargets,
    save,

    reasonOptions,
    downloadTarget,
    openDownload,
    closeDownload,
    confirmDownload,

    itemShipperCode,
    filesByFileId,
    mediaTarget,
    openMedia,
    closeMedia,
  }
}
